// Zero-asset Web-Audio ticker.  Emits a soft "click" (short sine + expo decay)
// whenever the caller signals a value has updated.  Kept opt-in via a small
// button in the terminal controls; state persists in localStorage.
import { useCallback, useEffect, useRef, useState } from "react";

const KEY = "vinci-pulse-sound";

export function useTickerSound() {
  const [enabled, setEnabled] = useState(() => {
    try { return localStorage.getItem(KEY) === "on"; } catch { return false; }
  });
  const ctxRef = useRef(null);

  useEffect(() => {
    return () => {
      if (ctxRef.current && ctxRef.current.state !== "closed") {
        ctxRef.current.close().catch(() => {});
      }
    };
  }, []);

  const toggle = useCallback(() => {
    setEnabled((cur) => {
      const next = !cur;
      try { localStorage.setItem(KEY, next ? "on" : "off"); } catch {}
      if (next && !ctxRef.current) {
        try {
          const Ctx = window.AudioContext || window.webkitAudioContext;
          if (Ctx) ctxRef.current = new Ctx();
        } catch {}
      }
      return next;
    });
  }, []);

  const click = useCallback((variant = "tick") => {
    if (!enabled) return;
    let ctx = ctxRef.current;
    if (!ctx) {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        ctx = new Ctx();
        ctxRef.current = ctx;
      } catch { return; }
    }
    if (ctx.state === "suspended") ctx.resume().catch(() => {});

    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    // Variant tuning — 'up' brighter, 'down' warmer, 'tick' neutral click
    const freq = variant === "up" ? 1200 : variant === "down" ? 640 : 880;
    osc.frequency.value = freq;
    osc.type = "sine";
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.15, now + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.09);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.11);
  }, [enabled]);

  return { enabled, toggle, click };
}
